import { Heading, Button, FormItem, Form } from '@lmc/ui-kit'
import { Divider, InputNumber, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { InputSuffix } from 'screens/Rostering/formComponents'
import {
  getCannotBeEmptyRule,
  getMinMaxRule,
  openGoogleMaps,
} from 'screens/Rostering/utils'
import { Form as AntdForm } from 'antd'
import { TeamMember, TravelMode } from 'screens/Rostering/types'
import { useState, useEffect, useMemo } from 'react'
import useEditTravelInfo from 'screens/Rostering/hooks/useEditTravelInfo'
import { Address } from '@lmc/ts-common'
import { getNewAddress } from 'screens/Rostering/RotaPattern/utils'
import { useGetTravelRateCardSettingsHome } from 'screens/Rostering/hooks/useTravelRateCardSettings'
import { capitalize } from 'lodash'

type EditTravelDetailsModalProps = {
  editTravelDetailsModalVisible: boolean
  handleCancel: () => void
  isLoadingUpdate?: boolean
  teamMember: TeamMember
  instanceId: string
  endAddress: Address
  manualEdit: boolean
}

const EditTravelDetailsModal = ({
  editTravelDetailsModalVisible,
  handleCancel,
  isLoadingUpdate,
  teamMember,
  instanceId,
  endAddress,
  manualEdit,
}: EditTravelDetailsModalProps) => {
  const [form] = AntdForm.useForm()

  const { t } = useTranslation()

  const { data: homeRateCard } = useGetTravelRateCardSettingsHome()

  const { editTravelInfo } = useEditTravelInfo({
    instanceId,
  })

  const { travelInfo } = teamMember

  const travelModes = homeRateCard?.travelModes || []

  const getInitialValues = () => ({
    travelTime: travelInfo ? travelInfo.travelTimeMinutes : 0,
    travelDistance: travelInfo ? travelInfo.distanceInMiles : 0.0,
    travelMode: travelInfo ? travelInfo.mode : TravelMode.DRIVING,
  })

  const [formData, setFormData] =
    useState<Record<string, any>>(getInitialValues())

  useEffect(() => {
    const initialValues = getInitialValues()
    setFormData(initialValues)
    form.setFieldsValue(initialValues)
  }, [editTravelDetailsModalVisible, travelInfo, form])

  const endAddressLabel = getNewAddress(endAddress, false)

  const startAddressLabel = travelInfo?.fromAddress
    ? getNewAddress(travelInfo.fromAddress, false)
    : ''

  const {
    isTravelModeChanged,
    isTravelTimeChanged,
    isTravelDistanceChanged,
    shouldShowRecalculationMessage,
  } = useMemo(() => {
    const initialValues = getInitialValues()
    const isTravelModeChanged = formData.travelMode !== initialValues.travelMode
    const isTravelTimeChanged = formData.travelTime !== initialValues.travelTime
    const isTravelDistanceChanged =
      formData.travelDistance !== initialValues.travelDistance

    const shouldShowRecalculationMessage =
      isTravelModeChanged && !isTravelTimeChanged && !isTravelDistanceChanged

    return {
      isTravelModeChanged,
      isTravelTimeChanged,
      isTravelDistanceChanged,
      shouldShowRecalculationMessage,
    }
  }, [formData, travelInfo])

  const handleSave = async () => {
    await form.validateFields()

    const payload: any = {
      teamMemberId: teamMember.teamMemberId,
      role: teamMember.role || '',
    }

    if (isTravelDistanceChanged) {
      payload.travelDistance = formData.travelDistance
    }
    if (isTravelTimeChanged) {
      payload.travelTime = formData.travelTime
    }
    if (isTravelModeChanged) {
      payload.travelMode = formData.travelMode
    }

    editTravelInfo(payload)

    handleCancel()
  }

  const handleFormValuesChange = (values: Record<string, any>) => {
    setFormData((prev) => ({ ...prev, ...values }))
  }

  return (
    <Modal
      width={580}
      open={editTravelDetailsModalVisible}
      onCancel={handleCancel}
      destroyOnClose
      footer={
        <FooterWrapper>
          <FooterRightSide>
            <Button onClick={handleCancel}>{t('CANCEL')}</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isLoadingUpdate}
            >
              {t('SAVE')}
            </Button>
          </FooterRightSide>
        </FooterWrapper>
      }
    >
      <StyledHeading level={2}>
        {manualEdit ? t('MANUAL_EDIT_DETAILS') : t('EDIT_TRAVEL_DETAILS')}
      </StyledHeading>

      <StyledDivider />

      <Container>
        <GrayWrapper>
          {!manualEdit && (
            <AddressSection onClick={() => openGoogleMaps(startAddressLabel)}>
              <GrayText level={2}>{t('START_LOCATION')}</GrayText>
              <UnderlineText>{startAddressLabel}</UnderlineText>
            </AddressSection>
          )}
          <AddressSection onClick={() => openGoogleMaps(endAddressLabel)}>
            <GrayText level={2}>{t('END_LOCATION')}</GrayText>
            <UnderlineText>{endAddressLabel}</UnderlineText>
          </AddressSection>
        </GrayWrapper>

        <Form
          form={form}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
          layout="vertical"
          validateTrigger={['']}
        >
          <FormItem
            name="travelMode"
            label={<Text>{t('TRAVEL_MODE')}</Text>}
            rules={[getCannotBeEmptyRule()]}
          >
            <StyledSelect placeholder={t('SELECT_TRAVEL_MODE')}>
              {travelModes.map((mode) => (
                <Select.Option key={mode.value} value={mode.value}>
                  <Text>{capitalize(mode.name)}</Text>
                </Select.Option>
              ))}
            </StyledSelect>
          </FormItem>

          {shouldShowRecalculationMessage && (
            <Text>{t('TRAVEL_TIME_AND_MILEAGE_RECALCULATED')}</Text>
          )}

          <FormItem
            name="travelTime"
            label={<Text>{t('TRAVEL_TIME')}</Text>}
            rules={[getCannotBeEmptyRule(), getMinMaxRule(1, 9999)]}
          >
            <StyledInputNumber
              suffix={
                <InputSuffix hasMargin={false}>
                  {t('MINUTES').toLocaleLowerCase()}
                </InputSuffix>
              }
              type="number"
              controls={false}
              precision={0}
            />
          </FormItem>
          <FormItem
            name="travelDistance"
            label={<Text>{t('TRAVEL_DISTANCE')}</Text>}
            rules={[getCannotBeEmptyRule(), getMinMaxRule(0.1, 9999.9)]}
          >
            <StyledInputNumber
              suffix={
                <InputSuffix hasMargin={false}>
                  {t('MILES').toLocaleLowerCase()}
                </InputSuffix>
              }
              type="number"
              controls={false}
              precision={1}
            />
          </FormItem>
        </Form>

        <Text>{t('DETAILS_PUBLISHED')}</Text>

        <StyledDivider />
      </Container>
    </Modal>
  )
}

export default EditTravelDetailsModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 24px;
`
const StyledDivider = styled(Divider)`
  margin: 16px 0;
`
const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
const FooterRightSide = styled.div`
  display: flex;
  gap: 8px;
`
const StyledInputNumber = styled(InputNumber)`
  width: 200px;
  height: 40px;
  margin-top: 4px;
  display: flex;
  align-items: center;
` as unknown as typeof InputNumber

const StyledSelect = styled(Select)`
  max-width: 200px;
  min-width: 200px;
  height: 40px;
  margin-top: 4px;
` as unknown as typeof Select

const GrayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`
const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
`
const UnderlineText = styled(Text)`
  text-decoration: underline;
`
